import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pause, Play, Repeat } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Spinner,
} from '@pnl/ui';
import { PnlApiError, type ReplicatorAdminItem, type ReplicatorStatusKey } from '@pnl/sdk';
import { api } from '../lib/api';

function fmtBrl(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';
}

const statusTone: Record<
  ReplicatorStatusKey,
  'neutral' | 'brand' | 'success' | 'warning' | 'danger'
> = {
  PENDING_PAYMENT: 'warning',
  ACTIVE: 'success',
  PAST_DUE: 'danger',
  CANCELED: 'neutral',
  EXPIRED: 'neutral',
};

const statusLabel: Record<ReplicatorStatusKey, string> = {
  PENDING_PAYMENT: 'Aguardando pagamento',
  ACTIVE: 'Ativo',
  PAST_DUE: 'Atrasado',
  CANCELED: 'Cancelado',
  EXPIRED: 'Expirado',
};

const FILTERS: { value: ReplicatorStatusKey | 'ALL'; label: string }[] = [
  { value: 'ACTIVE', label: 'Ativos' },
  { value: 'PAST_DUE', label: 'Atrasados' },
  { value: 'PENDING_PAYMENT', label: 'Aguardando' },
  { value: 'CANCELED', label: 'Cancelados' },
  { value: 'ALL', label: 'Todos' },
];

export function ReplicatorsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<ReplicatorStatusKey | 'ALL'>('ACTIVE');
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'replicators', filter],
    queryFn: () =>
      api.listReplicators({
        page: 1,
        pageSize: 100,
        ...(filter === 'ALL' ? {} : { status: filter }),
      }),
  });

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['admin', 'replicators'] });
  }

  const suspend = useMutation({
    mutationFn: (id: string) => api.suspendReplicatorAdmin(id),
    onSuccess: refresh,
    onError: (e) => setActionError(e instanceof PnlApiError ? e.api.message : 'Falha ao suspender'),
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => api.reactivateReplicatorAdmin(id),
    onSuccess: refresh,
    onError: (e) => setActionError(e instanceof PnlApiError ? e.api.message : 'Falha ao reativar'),
  });

  const items = query.data?.items ?? [];
  const busy = suspend.isPending || reactivate.isPending;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">Operação</p>
        <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
          Replicadores
        </h1>
        <p className="text-ink-500 mt-1 text-sm">
          Assinaturas pagas do replicador: status da mensalidade, próximo vencimento e
          suspender/reativar manualmente.
        </p>
      </div>

      {actionError && <Alert tone="danger">{actionError}</Alert>}

      <Card>
        <CardHeader>
          <div className="flex w-full items-center justify-between gap-4">
            <CardTitle>
              {items.length} replicador(es){query.isLoading ? '…' : ''}
            </CardTitle>
            <div className="flex gap-1">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFilter(f.value)}
                  className={
                    'rounded-md px-3 py-1.5 text-xs font-medium transition-colors ' +
                    (filter === f.value
                      ? 'bg-brand-500 text-white'
                      : 'text-ink-500 hover:bg-ink-100')
                  }
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {query.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Repeat size={28} />}
              title="Nenhum replicador nesta categoria"
              description="Assinaturas do replicador aparecem aqui conforme os traders ativam."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-ink-100 text-2xs text-ink-500 border-b text-left font-semibold uppercase tracking-widest">
                    <th className="px-3 py-2.5">Trader</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-2 py-2.5">Ativo desde</th>
                    <th className="px-2 py-2.5">Próx. cobrança</th>
                    <th className="px-2 py-2.5 text-right">Mensalidade</th>
                    <th className="px-3 py-2.5 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <ReplicatorRow
                      key={r.id}
                      r={r}
                      busy={busy}
                      onSuspend={() => {
                        setActionError(null);
                        suspend.mutate(r.id);
                      }}
                      onReactivate={() => {
                        setActionError(null);
                        reactivate.mutate(r.id);
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function ReplicatorRow({
  r,
  busy,
  onSuspend,
  onReactivate,
}: {
  r: ReplicatorAdminItem;
  busy: boolean;
  onSuspend: () => void;
  onReactivate: () => void;
}) {
  const canSuspend = r.status !== 'CANCELED' && r.status !== 'EXPIRED';
  const canReactivate = r.status === 'CANCELED' || r.status === 'PAST_DUE';
  return (
    <tr className="border-ink-100 border-b">
      <td className="px-3 py-2.5">
        <p className="text-ink-900 font-medium">{r.traderFullName || r.traderEmail}</p>
        <p className="text-ink-500 text-xs">{r.traderEmail}</p>
      </td>
      <td className="px-3 py-2.5">
        <Badge tone={statusTone[r.status]}>{statusLabel[r.status]}</Badge>
      </td>
      <td className="text-ink-600 px-2 py-2.5 text-xs tabular-nums">{fmtDate(r.startedAt)}</td>
      <td className="text-ink-600 px-2 py-2.5 text-xs tabular-nums">
        {fmtDate(r.nextBillingAt ?? r.openBillingDueDate)}
      </td>
      <td className="text-ink-900 px-2 py-2.5 text-right font-semibold tabular-nums">
        {fmtBrl(r.monthlyAmountCents)}
      </td>
      <td className="px-3 py-2.5">
        <div className="flex justify-end gap-2">
          {canSuspend && (
            <Button
              size="sm"
              variant="secondary"
              leadingIcon={<Pause size={13} />}
              loading={busy}
              onClick={onSuspend}
            >
              Suspender
            </Button>
          )}
          {canReactivate && (
            <Button
              size="sm"
              leadingIcon={<Play size={13} />}
              loading={busy}
              onClick={onReactivate}
            >
              Reativar
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}
